// src/oura/components/visibility/CompetitorsTab.tsx
//
// Who the answers name, on the same questions: the rivals that take questions
// from the business first, then the ones only ever named beside it. Built from
// the scan's own data (competitorWins joined to the rows, and the rivals the
// scan recorded in businessAudit.competitors), nothing else. Under an engine
// filter every count is that engine's answers only.
//
// TWO GROUPS, ONE FACT PER ROW (YW, 30 Sep 2026). The list is split under two
// group heads so the rivals that cost something are separated from the ones
// that are only ever named alongside, and each rival carries the single
// strongest thing known about how it wins. The detail beside it says how the
// rival wins, which of the questions it takes are new since the last scan that
// asked the same questions, and who else the same answers name. Every fact is
// counted in rivalFacts.ts from the rows the counts come from.
//
// Usability-tested 30 Sep 2026 (staff and merchant tasks, keyboard, four
// widths). The comments below name the finding each choice answers.

import { useEffect, useId, useRef, useState } from "react";
import { ChevronRightIcon, ExternalLinkIcon, InfoIcon } from "lucide-react";
import { Tooltip } from "@component/tooltip";
import { ArrowLink, Delta, FOCUS_RING, InnerStat, ListRow, ShowMore, TYPE, Tag } from "@oura/ui";
import { cn } from "../../lib/cn";
import { MERCHANT_V2 } from "../../merchantV2";
import { pageHref, shortDate } from "../../lib/oura";
import { namedOn, trendDelta, type EngineFilter, type VisEngine, type VisModel, type VisRival, type VisRow, beatsHint } from "../../visibility/model";
import { rivalFacts, rowFact, whyTheyWin, type RivalFacts } from "../../visibility/rivalFacts";
import type { PriorScanState } from "../../visibility/usePriorScan";

const SHOWN = 3;
const OTHERS_SHOWN = 8;
/** The breakpoint the list and the detail sit side by side from (xl). */
const SIDE_BY_SIDE = "(min-width: 1280px)";

/** A rival the scan kept but could not count, and no answer here names: a dash, never a 0. */
const uncounted = (r: VisRival) => !r.scored && r.on === 0;

/** "18 Aug": the comparison scan, as the column head and the detail say it. */
const dayMonth = (iso: string) => shortDate(iso).replace(/ \d{4}$/, "");

/** A dash that a screen reader hears as a word. */
function None({ label = "none" }: { label?: string }) {
    return (
        <>
            <span aria-hidden className="text-gray-400">–</span>
            <span className="sr-only">{label}</span>
        </>
    );
}

/** How a rival's "Named on" moved, as words. Null when there is nothing to compare. */
function movedText(moved: RivalFacts["moved"], since: string): string | null {
    if (!moved) return null;
    if (moved.isNew) return `New since ${since}`;
    if (moved.delta === 0) return `Same as ${since}`;
    return `${moved.delta > 0 ? "▲" : "▼"} ${Math.abs(moved.delta)} since ${since}`;
}

/**
 * A rival's change in "Named on" since the comparison scan. Deliberately not
 * green or red: a rival named more often is bad news for the business, and a
 * colour that means "good" on the business's own row would say the opposite
 * here. "New" is the one state worth a tag.
 */
function Movement({ moved, since }: { moved: RivalFacts["moved"]; since: string }) {
    if (!moved) return null;
    if (moved.isNew) return <Tag tone="blue">New</Tag>;
    if (moved.delta === 0) return <span className={cn(TYPE.tc, "text-gray-400")}>Same</span>;
    const up = moved.delta > 0;
    // The business's own triangle, in grey: one shape for a change down the
    // whole column, colour only where the change is the business's own.
    return (
        <span className={cn(TYPE.tc, "font-medium tabular-nums text-gray-600")}>
            <span aria-hidden>{up ? "▲" : "▼"} </span>
            <span className="sr-only">{up ? "Up " : "Down "}</span>
            {Math.abs(moved.delta)}
            <span className="sr-only"> since {since}</span>
        </span>
    );
}

function QuestionList({
    rows,
    fresh,
    onOpen,
}: {
    rows: VisRow[];
    /** Row keys taken for the first time since the comparison scan. */
    fresh?: Set<string>;
    onOpen: (row: VisRow) => void;
}) {
    // The new ones first: they are the easiest to take back. No status dot:
    // the heading already says whose questions these are, and a coloured dot
    // on every row asked the reader to work out what the colour meant (YW).
    const ordered = fresh && fresh.size > 0 ? [...rows].sort((a, b) => Number(fresh.has(b.key)) - Number(fresh.has(a.key))) : rows;
    const row = (r: VisRow) => (
        <ListRow
            key={r.id}
            title={r.text}
            right={fresh?.has(r.key) ? <Tag tone="blue">New</Tag> : undefined}
            onClick={() => onOpen(r)}
        />
    );
    return (
        <div className="mt-1">
            {ordered.slice(0, SHOWN).map(row)}
            <ShowMore count={ordered.length - SHOWN}>{ordered.slice(SHOWN).map(row)}</ShowMore>
        </div>
    );
}

/**
 * One block of the detail. Every block has the same heading, a hairline above
 * it and the same space around it, so the pane reads top to bottom as a few
 * clear steps instead of seven labels at one weight (YW, 30 Sep 2026). The
 * heading is dark and the text under it lighter: the heading leads.
 */
function Block({ title, note, action, children }: { title: React.ReactNode; note?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <section className="mt-5 border-t border-gray-100 pt-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h4 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                    {title}
                    {note && <span className={cn(TYPE.tc, "ml-2 font-normal text-gray-400")}>{note}</span>}
                </h4>
                {action}
            </div>
            {children}
        </section>
    );
}

/**
 * What the rival has that the business does not. The verified gap sentence
 * first; without one, the branded report's own paragraph on the rival, said
 * to be that; with neither, nothing rather than a guess.
 */
function WhatTheyHave({ rival, staff }: { rival: VisRival; staff: boolean }) {
    const text = rival.gap ?? rival.narrative;
    if (!text) return null;
    return (
        <Block title={`What they have that ${staff ? "this business doesn't" : "you don't"}`} note={rival.gap ? undefined : "From the report"}>
            <p className={cn(TYPE.tb, "m-0 mt-1.5 leading-6 text-gray-600")}>{text}</p>
        </Block>
    );
}

/**
 * Why they win, as a short read with the numbers behind it (YW, 30 Sep 2026):
 * a sentence or two built only from what the scan measured, then the shares
 * it rests on as a quiet list, then the answers' own words about the rival.
 * The shares are small on purpose: the tiles above are the numbers to read,
 * and four more large numbers here competed with them. For a rival only ever
 * named beside the business it is "How they show up", because "win" over a
 * harmless rival made owners doubt the group it sits in.
 */
function WhyTheyWin({ model, rival, facts, engine, staff, businessName }: {
    model: VisModel;
    rival: VisRival;
    facts: RivalFacts;
    engine: EngineFilter;
    staff: boolean;
    businessName: string;
}) {
    const read = whyTheyWin(model, rival, facts, engine, staff, businessName);
    if (!read.summary && read.stats.length === 0) return null;
    return (
        <Block title={rival.takes > 0 ? "Why they win" : "How they show up"}>
            {read.summary && <p className={cn(TYPE.tb, "m-0 mt-1.5 leading-6 text-gray-600")}>{read.summary}</p>}
            {read.stats.length > 0 && (
                <dl className="m-0 mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                    {read.stats.map((st) => (
                        <div key={st.label} title={st.detail} className={cn(TYPE.tc, "flex min-w-0 items-baseline gap-2")}>
                            <dd className="m-0 w-11 shrink-0 font-semibold tabular-nums text-gray-900">{st.value}</dd>
                            <dt className="m-0 min-w-0 text-gray-600">
                                {st.label}
                                <span className="sr-only"> ({st.detail})</span>
                            </dt>
                        </div>
                    ))}
                </dl>
            )}
            {read.described && (
                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>
                    The answers describe them as <q className="text-gray-700">{read.described.replace(/[.!]$/, "")}</q>.
                </p>
            )}
        </Block>
    );
}

/**
 * The other businesses the same answers name, as one quiet line. This
 * business is left out: its count is the "Both named" tile, and a second copy
 * of it here read as a different number (F17).
 */
function AlsoNamed({ facts }: { facts: RivalFacts }) {
    const others = facts.alongside.filter((a) => !a.you);
    if (others.length === 0) return null;
    return (
        <p className={cn(TYPE.tc, "m-0 mt-5 border-t border-gray-100 pt-4 text-gray-500")}>
            Also named in the same answers:{" "}
            {others.map((a, i) => (
                <span key={a.name}>
                    {i > 0 && " · "}
                    <span className="text-gray-700">{a.name}</span>{" "}
                    <span className="tabular-nums">({a.count}<span className="sr-only"> {a.count === 1 ? "question" : "questions"}</span>)</span>
                </span>
            ))}
        </p>
    );
}

/**
 * The plan's tasks for the questions this rival takes, each named, so the link
 * says what it opens. It used to read "Fix: write the page they win with"
 * whatever the task was, and opened the first task found (F1).
 */
function FixLinks({ rival, onFix }: { rival: VisRival; onFix: (taskId: string | null) => void }) {
    const tasks = new Map<string, string | null>();
    rival.wins.forEach((r) => {
        if (r.fixTaskId && !tasks.has(r.fixTaskId)) tasks.set(r.fixTaskId, r.fixTaskTitle);
    });
    const list = Array.from(tasks.entries()).slice(0, 2);
    return (
        <div className="mt-3 flex flex-col items-start gap-2">
            {list.length > 0 ? (
                list.map(([id, title]) => (
                    <ArrowLink key={id} onClick={() => onFix(id)}>
                        Fix: {title ?? "open the task"}
                    </ArrowLink>
                ))
            ) : (
                <ArrowLink onClick={() => onFix(null)}>No task covers these yet. See all fixes</ArrowLink>
            )}
        </div>
    );
}

/** "Since 18 Aug: named on 2 more · 3 new questions taken", or null. */
function sinceText(moved: RivalFacts["moved"], fresh: number, since: string): string | null {
    const parts: string[] = [];
    if (moved) {
        if (moved.isNew) return [`New since ${since}`, fresh > 0 ? `${fresh} ${fresh === 1 ? "question" : "questions"} taken` : null].filter(Boolean).join(" · ");
        parts.push(moved.delta === 0 ? "no change" : `named on ${Math.abs(moved.delta)} ${moved.delta > 0 ? "more" : "fewer"}`);
    }
    if (fresh > 0) parts.push(`${fresh} new ${fresh === 1 ? "question" : "questions"} taken`);
    return parts.length > 0 ? `Since ${since}: ${parts.join(" · ")}` : null;
}

function RivalDetail({
    model,
    engine,
    businessName,
    rival,
    facts,
    of,
    staff,
    since,
    onOpen,
    onLostTo,
    onFix,
}: {
    model: VisModel;
    engine: EngineFilter;
    businessName: string;
    rival: VisRival;
    facts: RivalFacts;
    /** The questions the counts are out of, under the engine filter. */
    of: number;
    staff: boolean;
    /** The comparison scan's date ("18 Aug"), when there is one to compare with. */
    since: string | null;
    onOpen: (row: VisRow) => void;
    onLostTo: (name: string) => void;
    onFix: (taskId: string | null) => void;
}) {
    const who = staff ? "it" : "you";
    const href = rival.domain ? pageHref(rival.domain) : null;
    const dash = uncounted(rival);
    // One quiet line under the tiles instead of two long tile captions: at
    // 1280 the caption wrapped and left "Aug" alone on a line (F12, F17).
    const sinceLine = since && !dash ? sinceText(facts.moved, facts.newWins.size, since) : null;
    return (
        <div className="min-w-0 px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h3 className={cn(TYPE.tt, "m-0 min-w-0 break-words text-gray-900")}>{rival.name}</h3>
                {href && rival.domain ? (
                    // Blue, because it is the one link here: grey read as a caption (F13).
                    <a href={href} target="_blank" rel="noopener noreferrer" className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700 hover:underline", FOCUS_RING)}>
                        {rival.domain}
                        <ExternalLinkIcon aria-hidden size={12} />
                        <span className="sr-only">(opens in a new tab)</span>
                    </a>
                ) : (
                    <span className={cn(TYPE.tc, "text-gray-500")}>No website on record</span>
                )}
            </div>
            {(rival.note || !rival.scored) && (
                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                    {[rival.note, rival.scored ? null : "Not scored in this run"].filter(Boolean).join(" · ")}
                </p>
            )}

            {/* Labels kept to one line: on a phone "Both named" wrapped and
                dropped its number below the other two. */}
            <div className="mt-4 grid grid-cols-3 gap-2">
                <InnerStat
                    label={<span className="whitespace-nowrap">Named on</span>}
                    value={<span className={TYPE.num}>{dash ? "–" : rival.on}</span>}
                    sub={dash ? undefined : `of ${of}`}
                />
                {/* The same words and the same dash as the table column: a
                    rival that takes nothing shows no "0" to read as a score. */}
                <InnerStat
                    label={<span className="whitespace-nowrap">Beats {who}</span>}
                    value={
                        rival.takes > 0 && !dash
                            ? <span className={cn(TYPE.num, "text-amber-800")}>{rival.takes}</span>
                            : <span className={cn(TYPE.num, "text-gray-400")}><None /></span>
                    }
                />
                <InnerStat label={<span className="whitespace-nowrap">Both named</span>} value={<span className={TYPE.num}>{dash ? "–" : rival.both.length}</span>} />
            </div>
            {sinceLine && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{sinceLine}</p>}

            <WhyTheyWin model={model} rival={rival} facts={facts} engine={engine} staff={staff} businessName={businessName} />
            <WhatTheyHave rival={rival} staff={staff} />

            {rival.wins.length > 0 ? (
                <Block title="Questions they take" action={<ArrowLink onClick={() => onLostTo(rival.name)}>All {rival.wins.length} in the table</ArrowLink>}>
                    <QuestionList rows={rival.wins} fresh={facts.newWins} onOpen={onOpen} />
                    {staff && <FixLinks rival={rival} onFix={onFix} />}
                </Block>
            ) : rival.both.length > 0 ? (
                // The group heading's own words (F7).
                <Block title={`Named beside ${who} on`}>
                    <QuestionList rows={rival.both} onOpen={onOpen} />
                </Block>
            ) : (
                <p className={cn(TYPE.tc, "m-0 mt-5 border-t border-gray-100 pt-5 text-gray-500")}>No answer on this scan names it.</p>
            )}

            <AlsoNamed facts={facts} />
        </div>
    );
}

/**
 * A group head inside the table, spanning every column: a grey band with a
 * rule above and below, the title at body size and bold, and what the group
 * means in words on the right. It was a dot and a 13px label, smaller than the
 * rows under it, so it read as one more row; and the dot's colour left owners
 * asking what amber meant against grey (YW, 30 Sep 2026).
 */
function GroupRow({ title, means, count, span }: { title: string; means: string; count: number; span: number }) {
    return (
        <tr>
            <th scope="colgroup" colSpan={span} className="border-y border-gray-200 bg-gray-50 px-5 py-2.5 text-left font-normal">
                <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>
                        {title}
                        <span className="ml-1.5 font-normal tabular-nums text-gray-400">{count}</span>
                    </span>
                    <span className={cn(TYPE.tc, "text-gray-500")}>{means}</span>
                </span>
            </th>
        </tr>
    );
}

/**
 * Pick one rival by name (#4026 C-FE-7).
 *
 * To see how one rival wins, a client had to find this tab and read a
 * two-group list. Typing three letters of a name now gets them there.
 *
 * It is a FILTER over the list the tab already shows -- `rivalsOn(model,
 * engine)`, in that list's own order, with the same "Named on" and "Beats it"
 * numbers beside each name, so the picker and the list can never disagree.
 * Choosing one does exactly what clicking a row does.
 *
 * NO ENGINE CONTROL of its own (#4025 FE-11). It follows the page's engine
 * switch and says which engine that is, in the same words the Questions tab
 * uses -- two controls for one setting is how they get out of step.
 *
 * It does not replace the list at any width: on a short list it is a faster
 * way to the same rows, and the rows stay as FE-14 leaves them.
 */
function RivalPicker({ rivals, current, engineLabel, who, onPick }: {
    rivals: VisRival[];
    current: VisRival;
    /** Null under All engines. */
    engineLabel: string | null;
    who: string;
    onPick: (name: string) => void;
}) {
    const [query, setQuery] = useState("");
    const listId = useId();
    const q = query.trim().toLowerCase();
    // Substring, not a fuzzy match: a rival list is short and a name typed
    // wrong should find nothing rather than something else.
    const found = q ? rivals.filter((r) => r.name.toLowerCase().includes(q)) : rivals;

    return (
        <div className="border-t border-gray-100 px-5 py-3">
            <label htmlFor={listId} className={cn(TYPE.lab, "mb-1.5 block")}>Find a competitor</label>
            <input
                id={listId}
                type="search"
                value={query}
                autoComplete="off"
                placeholder={`Type a name to narrow ${rivals.length} ${rivals.length === 1 ? "rival" : "rivals"}`}
                onChange={(e) => setQuery(e.target.value)}
                className={cn(TYPE.tb, "h-9 w-full max-w-sm rounded-xl border-0 bg-gray-50 px-3 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600")}
            />
            {/* The matches, as the list's own rows read: name, named on, beats.
                Shown only while narrowing -- unfiltered this would be the list
                twice over. */}
            {q.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Matching competitors">
                    {found.length === 0 ? (
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>No rival on this scan matches &ldquo;{query.trim()}&rdquo;.</p>
                    ) : (
                        found.map((r) => (
                                <button
                                    key={r.name}
                                    type="button"
                                    aria-pressed={r.name === current.name}
                                    onClick={() => onPick(r.name)}
                                    className={cn(
                                        TYPE.tc,
                                        "inline-flex h-[30px] max-w-full items-center gap-1.5 truncate rounded-full px-3 font-medium transition-colors",
                                        r.name === current.name
                                            ? "bg-gray-900 text-white"
                                            : "bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                                        FOCUS_RING,
                                    )}
                                >
                                    <span className="truncate">{r.name}</span>
                                    {/* `r.on` and `r.takes` are the very
                                        fields the rows below render, so the
                                        picker's two numbers cannot disagree
                                        with the list's -- they are the same
                                        numbers, not a second derivation. */}
                                    <span className={cn("tabular-nums", r.name === current.name ? "text-white/70" : "text-gray-400")}>
                                        {r.on}
                                        {r.takes > 0 && ` · ${r.takes}`}
                                    </span>
                                    <span className="sr-only">
                                        , named on {r.on} {r.on === 1 ? "question" : "questions"}
                                        {r.takes > 0 ? `, beats ${who} on ${r.takes}` : ""}
                                    </span>
                                </button>
                        ))
                    )}
                </div>
            )}
            {/* Which engine these rivals are from, and where that is set: the
                same sentence the Questions tab shows (#4025 FE-11). */}
            {engineLabel && (
                <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                    Showing <span className="font-medium text-gray-700">{engineLabel}</span> only.
                    Use the engine switch at the top of the page to change it.
                </p>
            )}
        </div>
    );
}

export function CompetitorsTab({
    model,
    rivals,
    engine,
    staff,
    businessName,
    priorScan,
    selected,
    onSelect,
    onOpen,
    onLostTo,
    onFix,
}: {
    model: VisModel;
    /** The rivals as the engine filter counts them (`rivalsOn`). */
    rivals: VisRival[];
    engine: EngineFilter;
    staff: boolean;
    businessName: string;
    /** The previous scan that asked the same questions, and whether it loaded. */
    priorScan: PriorScanState;
    selected: string | null;
    onSelect: (name: string) => void;
    onOpen: (row: VisRow) => void;
    onLostTo: (name: string) => void;
    /** A task id, or null for the Fixes list. */
    onFix: (taskId: string | null) => void;
}) {
    const detailRef = useRef<HTMLDivElement>(null);
    const prior = priorScan.status === "ready" ? priorScan.prior : null;
    const priorDay = priorScan.capturedAt ? dayMonth(priorScan.capturedAt) : null;
    const picked = useRef(false);
    const current = rivals.find((r) => r.name === selected) ?? rivals[0] ?? null;

    // Stacked below 1280px, a new rival's details land under the list, out of
    // sight on a phone. Bring them up, but only after a pick, never on load (F11).
    useEffect(() => {
        if (!picked.current || !current) return;
        picked.current = false;
        if (!window.matchMedia(SIDE_BY_SIDE).matches) {
            detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [current?.name]); // eslint-disable-line react-hooks/exhaustive-deps

    if (rivals.length === 0 || !current) {
        return (
            <p className={cn(TYPE.tb, "m-0 border-t border-gray-100 px-5 py-8 text-gray-500")}>
                {engine === "all"
                    ? "No rival was named in any answer on this scan."
                    : `No rival was named in any ${model.engines[engine].label} answer on this scan.`}
            </p>
        );
    }
    const pick = (name: string) => {
        picked.current = true;
        onSelect(name);
    };
    const who = staff ? "it" : "you";
    // The tooltip is the merchant redesign's; with its flag off a merchant
    // keeps the line over the table, as staff do.
    const beatsTip = !staff && MERCHANT_V2;
    // What the Beats column counts: a line over the table for staff, the
    // header's tooltip for a merchant.
    const beatsText = engine === "all"
        ? beatsHint(staff, model.counted)
        : `counts the questions where ${model.engines[engine].label} names them and not ${who}.`;
    const engineLabel = (e: VisEngine) => model.engines[e].label;
    const you = namedOn(model, engine);
    // Out of the questions the filtered engine has a reading for, as its
    // counts are: fifty for every engine on a current scan (#3945).
    const of = engine === "all" ? model.rows.length : model.rows.filter((r) => r.cells[engine] !== null).length;

    // Movement is an all-engines comparison against a scan that asked the same
    // questions. One engine's figure from last month was never stored alone.
    const compare = engine === "all" && prior ? prior : null;
    const since = compare ? dayMonth(compare.capturedAt) : null;
    const ownDelta = engine === "all" ? trendDelta(model.trend, "all") : null;

    const facts = new Map(rivals.map((r) => [r.name, rivalFacts(model, r, engine, businessName, compare)]));
    const taking = rivals.filter((r) => r.takes > 0);
    const beside = rivals.filter((r) => r.takes === 0);
    const span = compare ? 5 : 4;

    const row = (r: VisRival) => {
        const f = facts.get(r.name)!;
        const line = r.scored ? rowFact(f, r, who, engineLabel) : "Not scored in this run";
        const isCurrent = r.name === current.name;
        const moved = since ? movedText(f.moved, since) : null;
        return (
            <tr
                key={r.name}
                onClick={() => pick(r.name)}
                // A tinted row and a blue edge: the near-white grey alone was
                // hard to see at every width (F11).
                className={cn(
                    "cursor-pointer",
                    isCurrent ? "bg-blue-50/70 shadow-[inset_3px_0_0_#0B49FE]" : "hover:bg-gray-50",
                )}
            >
                <td className="h-[60px] border-t border-gray-100 py-2 pl-5 pr-3">
                    <button
                        type="button"
                        aria-pressed={isCurrent}
                        aria-controls="competitor-detail"
                        onClick={(e) => { e.stopPropagation(); pick(r.name); }}
                        className={cn(TYPE.tb, "block w-full truncate rounded text-left font-medium text-gray-900", FOCUS_RING)}
                    >
                        {r.name}
                    </button>
                    {line && <span className={cn(TYPE.tm, "block truncate text-gray-500")} title={line}>{line}</span>}
                    {/* The "vs" column is hidden on a phone, where the names need
                        its width; the change gets its own line there instead. */}
                    {moved && <span className={cn(TYPE.tm, "mt-0.5 block text-gray-500 sm:hidden")}>{moved}</span>}
                </td>
                {/* The number alone: "of 50" on every row made the column
                    ragged and said again what the header already says. */}
                <td className="border-t border-gray-100 px-3 text-center">
                    {uncounted(r) ? (
                        <None label="not counted" />
                    ) : (
                        <span className={cn(TYPE.tb, "tabular-nums text-gray-900")}>
                            {r.on}
                            <span className="sr-only"> of {of}</span>
                        </span>
                    )}
                </td>
                <td className="border-t border-gray-100 pl-3 pr-5 text-center sm:pr-3">
                    {r.takes > 0
                        ? <span className={cn(TYPE.tb, "font-semibold tabular-nums text-amber-800")}>{r.takes}</span>
                        : <None />}
                </td>
                {compare && (
                    <td className="hidden border-t border-gray-100 px-3 text-right sm:table-cell">
                        <Movement moved={f.moved} since={since!} />
                    </td>
                )}
                <td className="hidden border-t border-gray-100 pr-4 text-right sm:table-cell">
                    <ChevronRightIcon aria-hidden size={14} className="ml-auto text-gray-400" />
                </td>
            </tr>
        );
    };

    return (
        <div>
            {/* One filter row per tab, in the same place and the same style as
                the Questions tab's (#4025 FE-11, #4026 C-FE-7). The tester's
                note ("too many navigation points") and FE-11 pull opposite ways
                -- fewer controls against one more filter -- and the answer is
                that both land as ONE pattern rather than two. */}
            <RivalPicker
                rivals={rivals}
                current={current}
                engineLabel={engine === "all" ? null : model.engines[engine].label}
                who={who}
                onPick={pick}
            />
            {/* Under one engine the counts change meaning and the comparison
                goes; the tab says so instead of letting numbers move silently (F3, F4). */}
            {/* The comparison scan loading or failing is said, never left to
                read as "nothing changed" (Jack's review on #3952). */}
            {engine === "all" && priorScan.status === "loading" && (
                <p aria-live="polite" className={cn(TYPE.tc, "m-0 border-t border-gray-100 px-5 py-2.5 text-gray-500")}>
                    Loading the {priorDay ?? "previous"} scan to show what changed.
                </p>
            )}
            {engine === "all" && priorScan.status === "error" && (
                <p role="alert" className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-gray-100 bg-gray-50 px-5 py-2.5 text-gray-700")}>
                    Could not load the {priorDay ?? "previous"} scan, so the change since then is not shown.
                    <button type="button" onClick={priorScan.retry} className={cn("rounded font-medium text-blue-600 hover:text-blue-700 hover:underline", FOCUS_RING)}>
                        Try again
                    </button>
                </p>
            )}
            {engine !== "all" && (
                <p className={cn(TYPE.tc, "m-0 border-t border-gray-100 bg-gray-50 px-5 py-2.5 text-gray-600")}>
                    <span className="font-medium text-gray-900">{model.engines[engine].label} only.</span>{" "}
                    Counted from its answers to {of} questions, so the numbers differ from all engines.
                    {prior ? ` The change since ${dayMonth(prior.capturedAt)} shows on All AIs.` : ""}
                </p>
            )}
            {/* What the column counts, on screen. It was a native `title` and
                a screen-reader span, which nobody meets on touch and few find
                on a desktop -- so the one number on the tab that needs
                explaining had no explanation anyone saw (#4025 FE-15). */}
            {!beatsTip && (
                <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 px-5 pt-2.5 text-gray-500")}>
                    <span className="font-medium text-gray-600">Beats {who}</span> {beatsText}
                </p>
            )}
            {/* Stacked below 1280px: beside the detail, the brand column was a
                few letters wide at 1024. */}
            <div className="grid grid-cols-1 border-t border-gray-100 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
                <div className="min-w-0">
                    <table className={cn(TYPE.tc, "w-full table-fixed border-collapse")}>
                        {/* Narrower number columns on a phone, where the brand
                            name needs the width more than "of 50" does. */}
                        {/* Three number columns of one width, so the gaps between
                            them are even whatever the numbers are. */}
                        <colgroup>
                            <col />
                            <col className="w-[76px] sm:w-[92px]" />
                            <col className="w-[76px] sm:w-[92px]" />
                            {compare && <col className="w-0 sm:w-[92px]" />}
                            <col className="w-0 sm:w-9" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col" className="py-3 pl-5 pr-3 text-left font-medium text-gray-500">Brand</th>
                                <th scope="col" title={`Out of ${of} questions`} className="px-3 py-3 text-center font-medium text-gray-500">
                                    Named on
                                    <span className="sr-only"> (out of {of} questions)</span>
                                </th>
                                <th scope="col" className="py-3 pl-3 pr-5 text-center font-medium text-gray-500 sm:whitespace-nowrap sm:pr-3">
                                    {!beatsTip ? (
                                        `Beats ${who}`
                                    ) : (
                                        // The merchant reads what it counts on hover, not in a line over the table.
                                        <span className="inline-flex items-center gap-1">
                                            Beats {who}
                                            <Tooltip side="top" asChild content={beatsText.charAt(0).toUpperCase() + beatsText.slice(1)}>
                                                <button type="button" aria-label={`What Beats ${who} counts`} className={cn("rounded-full text-gray-400 hover:text-gray-600", FOCUS_RING)}>
                                                    <InfoIcon aria-hidden size={13} />
                                                </button>
                                            </Tooltip>
                                        </span>
                                    )}
                                </th>
                                {compare && (
                                    <th scope="col" className="hidden whitespace-nowrap px-3 py-3 text-right font-medium text-gray-500 sm:table-cell">
                                        <span aria-hidden>vs {since}</span>
                                        <span className="sr-only">Change since {since}</span>
                                    </th>
                                )}
                                <th scope="col" className="hidden sm:table-cell"><span className="sr-only">Open</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="h-[60px] border-t border-gray-100 py-2 pl-5 pr-3">
                                    <span className={cn(TYPE.tb, "block truncate font-semibold text-blue-600")}>{staff ? businessName : "You"}</span>
                                    {/* The business's own change on a phone, where its column is hidden (F12). */}
                                    {compare && ownDelta?.comparable && (
                                        <span className={cn(TYPE.tm, "mt-0.5 block sm:hidden")}>
                                            <Delta value={ownDelta.value} comparable={ownDelta.comparable} /> <span className="text-gray-500">since {since}</span>
                                        </span>
                                    )}
                                </td>
                                <td className="border-t border-gray-100 px-3 text-center">
                                    {you ? (
                                        <span className={cn(TYPE.tb, "font-semibold tabular-nums text-gray-900")}>
                                            {you.named}
                                            <span className="sr-only"> of {you.of}</span>
                                        </span>
                                    ) : (
                                        <None label="not measured" />
                                    )}
                                </td>
                                <td className="border-t border-gray-100 pl-3 pr-5 text-center sm:pr-3"><None label="not applicable" /></td>
                                {compare && (
                                    <td className={cn(TYPE.tc, "hidden border-t border-gray-100 px-3 text-right sm:table-cell")}>
                                        {/* The business's own line uses the page's delta, which
                                            already refuses a comparison across a change of method. */}
                                        {ownDelta ? (
                                            <>
                                                <Delta value={ownDelta.value} comparable={ownDelta.comparable} />
                                                <span className="sr-only"> since {since}</span>
                                            </>
                                        ) : null}
                                    </td>
                                )}
                                <td className="hidden border-t border-gray-100 sm:table-cell" />
                            </tr>
                            {taking.length > 0 && <GroupRow title="Named instead" means={staff ? "Named where it is not" : "Named where you are not"} count={taking.length} span={span} />}
                            {taking.map(row)}
                            {beside.length > 0 && <GroupRow title="Named beside" means={staff ? "Only ever named alongside it" : "Only ever named alongside you"} count={beside.length} span={span} />}
                            {beside.map(row)}
                        </tbody>
                    </table>
                    {engine === "all" && model.otherRivals.length > 0 && (
                        // The long tail each engine named, kept so no name the scan
                        // recorded goes missing. The backend counts none of them.
                        <div className="border-t border-gray-100 px-5 py-4">
                            <p className={cn(TYPE.lab, "m-0")}>Also named in the answers</p>
                            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>{model.otherRivals.slice(0, OTHERS_SHOWN).join(" · ")}</p>
                            <ShowMore count={model.otherRivals.length - OTHERS_SHOWN}>
                                <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{model.otherRivals.slice(OTHERS_SHOWN).join(" · ")}</p>
                            </ShowMore>
                        </div>
                    )}
                </div>
                <div
                    id="competitor-detail"
                    ref={detailRef}
                    aria-label={`${current.name}, details`}
                    className="min-w-0 scroll-mt-4 border-t border-gray-100 xl:border-l xl:border-t-0"
                >
                    {/* A short announcement when a pick changes the details, so a
                        screen reader hears the switch without the whole panel
                        being read out again (F9). */}
                    <p className="sr-only" aria-live="polite">Showing {current.name}</p>
                    <RivalDetail
                        key={current.name}
                        model={model}
                        engine={engine}
                        businessName={businessName}
                        rival={current}
                        facts={facts.get(current.name)!}
                        of={of}
                        staff={staff}
                        since={since}
                        onOpen={onOpen}
                        onLostTo={onLostTo}
                        onFix={onFix}
                    />
                </div>
            </div>
        </div>
    );
}
